import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { BarChart3, TrendingUp, Users, DollarSign, AlertTriangle, Download, FileSpreadsheet, FileText, FileJson, RefreshCw, MessageCircle, PhoneOff, Flame, CheckCircle2, Send, Banknote, Users2 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, PieChart, Pie, Legend } from "recharts";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { loadPalmaLogo, drawPalmaFooter, fechaHoraVE } from "../lib/pdfFooter";

const fmt = (n) => Number(n || 0).toLocaleString("es-VE", { maximumFractionDigits: 2 });
const hace = (iso) => {
  if (!iso) return null;
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "hace segundos";
  if (diff < 3600) return `hace ${Math.floor(diff/60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff/3600)} h`;
  if (diff < 604800) return `hace ${Math.floor(diff/86400)} d`;
  return new Date(iso).toLocaleDateString("es-VE", { timeZone: "America/Caracas" });
};

function KPI({ icon: Icon, label, value, hint, color = "green" }) {
  const bg = { green: "bg-green-50 border-green-100 text-green-800", orange: "bg-orange-50 border-orange-100 text-orange-800", blue: "bg-blue-50 border-blue-100 text-blue-800", red: "bg-red-50 border-red-100 text-red-800" }[color];
  return (
    <Card className={`p-4 border ${bg}`} data-testid={`kpi-${label.toLowerCase().replace(/\s/g,"-")}`}>
      <div className="flex items-start justify-between gap-2">
        <div><p className="text-xs uppercase font-medium opacity-70">{label}</p><p className="text-2xl font-bold mt-1">{value}</p>{hint && <p className="text-xs opacity-60 mt-1">{hint}</p>}</div>
        <Icon className="w-8 h-8 opacity-30" />
      </div>
    </Card>
  );
}

export default function ReportesPage() {
  const { token, API, hasPermission } = useAuth();
  const canExport = hasPermission ? hasPermission("reportes.exportar") : true;
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [morosos, setMorosos] = useState(null);
  const [loadingMor, setLoadingMor] = useState(false);
  const [colFilters, setColFilters] = useState({});
  const [f, setF] = useState({ desde: "", hasta: "", tipo: "todos", grupo: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (f.desde) params.desde = f.desde;
      if (f.hasta) params.hasta = f.hasta;
      if (f.tipo !== "todos") params.tipo = f.tipo;
      if (f.grupo) params.grupo = f.grupo;
      const r = await axios.get(`${API}/reportes/recaudacion-por-actividad`, { ...auth, params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar reporte");
    }
    setLoading(false);
  };
  const loadMorosos = async () => {
    setLoadingMor(true);
    try {
      const params = { limit: 20 };
      if (f.tipo !== "todos") params.tipo = f.tipo;
      const r = await axios.get(`${API}/reportes/morosidad-top`, { ...auth, params });
      setMorosos(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar ranking de morosidad");
    }
    setLoadingMor(false);
  };

  const marcarContactado = async (m, canal = "whatsapp", nota = "") => {
    try {
      await axios.post(`${API}/reportes/morosidad/${m.contribuyente_id}/contactar`,
        { canal, nota, deuda_snapshot: m.deuda_total }, auth);
      // Update local row optimistically
      setMorosos(prev => prev ? {
        ...prev,
        rows: prev.rows.map(r => r.contribuyente_id === m.contribuyente_id
          ? { ...r, ultimo_contacto: new Date().toISOString(), ultimo_contacto_canal: canal, total_intentos_contacto: (r.total_intentos_contacto || 0) + 1 }
          : r)
      } : prev);
    } catch (e) {
      console.warn("No se pudo registrar contacto:", e);
    }
  };

  const exportarMorososMasivo = async () => {
    try {
      const params = { limit: 1000 };
      if (f.tipo !== "todos") params.tipo = f.tipo;
      const r = await axios.get(`${API}/reportes/morosidad-top`, { ...auth, params });
      const all = r.data.rows || [];
      if (!all.length) { toast.error("No hay deudores para exportar"); return; }
      const ws = XLSX.utils.json_to_sheet(all.map((m, i) => ({
        "#": i + 1,
        "Contribuyente": m.nombre,
        "RIF": m.rif,
        "N° LAE": m.numero_lae,
        "Actividad": m.actividad,
        "Celular": m.celular,
        "Planillas pendientes": m.planillas_pendientes,
        "Deuda (Bs.)": m.deuda_total,
        "Períodos": (m.periodos || []).join(", "),
        "Último contacto": m.ultimo_contacto ? new Date(m.ultimo_contacto).toLocaleString("es-VE") : "—",
        "Canal último contacto": m.ultimo_contacto_canal || "—",
        "Intentos previos": m.total_intentos_contacto || 0,
        "Link WhatsApp": m.whatsapp_url,
      })));
      // Anchos de columna
      ws["!cols"] = [{wch:5},{wch:35},{wch:15},{wch:14},{wch:22},{wch:14},{wch:10},{wch:14},{wch:22},{wch:20},{wch:14},{wch:10},{wch:60}];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Deudores");
      const stamp = new Date().toISOString().replace(/[:.]/g,"-").slice(0,19);
      XLSX.writeFile(wb, `ranking_morosidad_${stamp}.xlsx`);
      toast.success(`Excel con ${all.length} deudores descargado`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al exportar deudores");
    }
  };
  useEffect(() => { load(); loadMorosos(); /* eslint-disable-next-line */ }, []);

  const rows = data?.rows || [];
  const kpis = data?.kpis || {};

  // Filtros por columna
  const setCol = (k, v) => setColFilters(p => ({ ...p, [k]: v }));
  const rowsFiltered = rows.filter(r =>
    (!colFilters.actividad || (r.actividad || "").toLowerCase().includes(colFilters.actividad.toLowerCase())) &&
    (!colFilters.codigo || (r.codigo || "").includes(colFilters.codigo)) &&
    (!colFilters.grupo || (r.grupo || "").toLowerCase().includes(colFilters.grupo.toLowerCase()))
  );

  // Top 10 para gráfica
  const top10 = rowsFiltered.slice(0, 10).map(r => ({ actividad: (r.actividad || "").slice(0, 22), recaudado: r.recaudado, esperado: r.esperado }));

  const exportar = async (formato) => {
    if (!rowsFiltered.length) { toast.error("No hay datos"); return; }
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const fn = `reporte_recaudacion_actividad_${stamp}`;
    if (formato === "excel") {
      const ws = XLSX.utils.json_to_sheet(rowsFiltered.map(r => ({
        "Código": r.codigo, "Tipo": r.tipo, "Grupo": r.grupo, "Actividad Específica": r.actividad,
        "Contribuyentes": r.n_contribuyentes, "Pagos": r.n_pagos,
        "Recaudado (Bs.)": r.recaudado, "Esperado (Bs.)": r.esperado,
        "% Morosidad": r.morosidad_pct, "Ticket Promedio (Bs.)": r.ticket_promedio,
      })));
      const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Recaudación por Actividad");
      XLSX.writeFile(wb, `${fn}.xlsx`);
      toast.success(`Excel descargado (${rowsFiltered.length} filas)`);
    } else if (formato === "json") {
      const blob = new Blob([JSON.stringify({ kpis, filtros: f, rows: rowsFiltered }, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${fn}.json`; a.click(); URL.revokeObjectURL(url);
      toast.success("JSON descargado");
    } else if (formato === "pdf") {
      const logo = await loadPalmaLogo();
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const { fecha, hora } = fechaHoraVE();
      doc.setFontSize(16); doc.setTextColor(20, 83, 45);
      doc.text("SISAUR · Reporte de Recaudación por Actividad Específica", 40, 40);
      doc.setFontSize(10); doc.setTextColor(80);
      doc.text(`Generado: ${fecha} ${hora}  ·  Filtros: ${f.desde || "sin fecha desde"} → ${f.hasta || "sin fecha hasta"}  ·  Tipo: ${f.tipo}`, 40, 58);
      doc.setFontSize(11); doc.setTextColor(20, 83, 45);
      doc.text(`Recaudado: Bs. ${fmt(kpis.total_recaudado)}   ·   Esperado: Bs. ${fmt(kpis.total_esperado)}   ·   Morosidad: ${kpis.morosidad_pct}%   ·   Pagos: ${kpis.n_pagos}   ·   Contribuyentes: ${kpis.n_contribuyentes}`, 40, 78);
      autoTable(doc, {
        head: [["Cód", "Tipo", "Grupo", "Actividad", "Contrib.", "Pagos", "Recaudado", "Esperado", "% Mora"]],
        body: rowsFiltered.map(r => [r.codigo, r.tipo, (r.grupo || "").slice(0, 22), (r.actividad || "").slice(0, 35), r.n_contribuyentes, r.n_pagos, fmt(r.recaudado), fmt(r.esperado), `${r.morosidad_pct}%`]),
        startY: 95, margin: { bottom: 60 },
        styles: { fontSize: 8, cellPadding: 4 },
        headStyles: { fillColor: [20, 83, 45], textColor: 255 },
        alternateRowStyles: { fillColor: [240, 253, 244] },
        columnStyles: { 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" }, 8: { halign: "right" } },
        didDrawPage: (d) => drawPalmaFooter(doc, { logoDataURL: logo, fecha, hora, pageNumber: d.pageNumber }),
      });
      doc.save(`${fn}.pdf`);
      toast.success("PDF descargado");
    }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-green-100 text-green-800"><BarChart3 className="w-6 h-6" /></div>
        <div><h1 className="font-display font-bold text-2xl text-green-900">Recaudación por Actividad · Morosidad</h1><p className="text-sm text-green-700/70">Análisis de recaudación por actividad económica y ranking de deudores</p></div>
      </div>

      <Card className="p-4 border-green-100">
        <p className="text-sm font-medium text-green-900 mb-3">Filtros del reporte</p>
        <div className="grid md:grid-cols-5 gap-3">
          <div><Label className="text-xs">Desde</Label><Input type="date" data-testid="rep-desde" value={f.desde} onChange={e => setF({ ...f, desde: e.target.value })} /></div>
          <div><Label className="text-xs">Hasta</Label><Input type="date" data-testid="rep-hasta" value={f.hasta} onChange={e => setF({ ...f, hasta: e.target.value })} /></div>
          <div><Label className="text-xs">Tipo de tabulador</Label>
            <Select value={f.tipo} onValueChange={v => setF({ ...f, tipo: v })}><SelectTrigger data-testid="rep-tipo"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="todos">Todos</SelectItem><SelectItem value="no-industrial">No Industrial</SelectItem><SelectItem value="industrial">Industrial</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Grupo/Clasificación</Label><Input data-testid="rep-grupo" value={f.grupo} onChange={e => setF({ ...f, grupo: e.target.value })} placeholder="ej. 6.1 o 13.1" /></div>
          <div className="flex items-end gap-2">
            <Button data-testid="rep-aplicar" onClick={() => { load(); loadMorosos(); }} disabled={loading} className="bg-green-700 hover:bg-green-800 text-white flex-1"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />Aplicar</Button>
            <Popover>
              <PopoverTrigger asChild><Button variant="outline" data-testid="rep-exportar" disabled={!rowsFiltered.length || !canExport} title={!canExport ? "Requiere permiso reportes.exportar" : ""}><Download className="w-4 h-4" /></Button></PopoverTrigger>
              <PopoverContent align="end" className="w-52 p-1">
                <button data-testid="rep-export-excel" onClick={() => exportar("excel")} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-green-50 text-left"><FileSpreadsheet className="w-4 h-4 text-green-700" /> Excel (.xlsx)</button>
                <button data-testid="rep-export-pdf" onClick={() => exportar("pdf")} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-orange-50 text-left"><FileText className="w-4 h-4 text-orange-600" /> PDF (.pdf)</button>
                <button data-testid="rep-export-json" onClick={() => exportar("json")} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-slate-50 text-left"><FileJson className="w-4 h-4 text-slate-600" /> JSON (.json)</button>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-4 gap-4">
        <KPI icon={DollarSign}       label="Total Recaudado"    value={`Bs. ${fmt(kpis.total_recaudado)}`}  hint={`de Bs. ${fmt(kpis.total_esperado)} esperados`} color="green" />
        <KPI icon={Users}            label="Contribuyentes"     value={kpis.n_contribuyentes || 0}          hint="activos con LAE en el reporte" color="blue" />
        <KPI icon={TrendingUp}       label="Nº Pagos"           value={kpis.n_pagos || 0}                   hint={`ticket prom. Bs. ${fmt(kpis.ticket_promedio)}`} color="orange" />
        <KPI icon={AlertTriangle}    label="% Morosidad"        value={`${kpis.morosidad_pct || 0}%`}       hint={kpis.morosidad_pct > 40 ? "Alto" : kpis.morosidad_pct > 20 ? "Medio" : "Bajo"} color="red" />
      </div>

      <Card className="p-4 border-green-100">
        <p className="font-medium text-green-900 mb-3">Top 10 actividades por recaudación</p>
        <div className="h-64" data-testid="rep-chart-top10">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={top10} layout="vertical" margin={{ left: 20, right: 20, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" tickFormatter={fmt} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="actividad" width={180} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => `Bs. ${fmt(v)}`} />
              <Bar dataKey="recaudado" fill="#16A34A" radius={[0, 4, 4, 0]}>
                {top10.map((_, i) => <Cell key={i} fill={i === 0 ? "#EA580C" : "#16A34A"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-4 border-green-100">
        <p className="font-medium text-green-900 mb-3">Detalle por Actividad Específica <span className="text-sm font-normal text-green-700/70">({rowsFiltered.length} filas)</span></p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-green-50 text-green-900">
              <tr>
                <th className="text-left p-2">Código</th>
                <th className="text-left p-2">Tipo</th>
                <th className="text-left p-2">Grupo</th>
                <th className="text-left p-2">Actividad</th>
                <th className="text-right p-2">Contrib.</th>
                <th className="text-right p-2">Pagos</th>
                <th className="text-right p-2">Recaudado (Bs.)</th>
                <th className="text-right p-2">Esperado (Bs.)</th>
                <th className="text-right p-2">% Mora</th>
              </tr>
              <tr className="bg-white">
                <th className="p-1.5"><Input value={colFilters.codigo || ""} onChange={e => setCol("codigo", e.target.value)} placeholder="1.1…" className="h-7 text-xs font-mono" data-testid="colf-codigo" /></th>
                <th></th>
                <th className="p-1.5"><Input value={colFilters.grupo || ""} onChange={e => setCol("grupo", e.target.value)} placeholder="Filtrar…" className="h-7 text-xs" /></th>
                <th className="p-1.5"><Input value={colFilters.actividad || ""} onChange={e => setCol("actividad", e.target.value)} placeholder="Filtrar…" className="h-7 text-xs" data-testid="colf-actividad" /></th>
                <th colSpan={5}></th>
              </tr>
            </thead>
            <tbody>
              {rowsFiltered.map((r, i) => (
                <tr key={i} className="border-b border-green-50 hover:bg-green-50/40">
                  <td className="p-2 font-mono text-xs">{r.codigo}</td>
                  <td className="p-2"><span className={`text-xs px-2 py-0.5 rounded ${r.tipo === "industrial" ? "bg-orange-100 text-orange-800" : "bg-green-100 text-green-800"}`}>{r.tipo}</span></td>
                  <td className="p-2 text-xs">{r.grupo}</td>
                  <td className="p-2">{r.actividad}</td>
                  <td className="p-2 text-right font-mono">{r.n_contribuyentes}</td>
                  <td className="p-2 text-right font-mono">{r.n_pagos}</td>
                  <td className="p-2 text-right font-mono font-bold text-green-800">{fmt(r.recaudado)}</td>
                  <td className="p-2 text-right font-mono text-slate-600">{fmt(r.esperado)}</td>
                  <td className={`p-2 text-right font-mono font-bold ${r.morosidad_pct > 50 ? "text-red-600" : r.morosidad_pct > 20 ? "text-orange-600" : "text-green-700"}`}>{r.morosidad_pct}%</td>
                </tr>
              ))}
              {!rowsFiltered.length && <tr><td colSpan={9} className="p-8 text-center text-slate-400">Sin datos para los filtros aplicados</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ============ Ranking Top 20 Morosidad + WhatsApp Cobranza ============ */}
      <Card className="p-4 border-orange-200 bg-gradient-to-br from-orange-50/40 to-white" data-testid="rep-morosidad-card">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded bg-orange-100 text-orange-700"><Flame className="w-4 h-4" /></div>
            <div>
              <p className="font-medium text-orange-900">Top 20 · Ranking de morosidad</p>
              <p className="text-xs text-orange-700/70">Contribuyentes con mayor deuda pendiente · gestión rápida por WhatsApp</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {morosos?.kpis && (
              <div className="text-right">
                <p className="text-xs text-orange-700/70">Deuda concentrada</p>
                <p className="text-xl font-bold text-orange-800">Bs. {fmt(morosos.kpis.deuda_total)}</p>
              </div>
            )}
            <Button variant="outline" size="sm" onClick={exportarMorososMasivo} disabled={loadingMor || !morosos?.rows?.length} data-testid="rep-morosidad-export-masivo" className="border-orange-300 text-orange-700 hover:bg-orange-100">
              <Send className="w-3.5 h-3.5 mr-1" /> Exportar todos
            </Button>
            <Button variant="outline" size="sm" onClick={loadMorosos} disabled={loadingMor} data-testid="rep-morosidad-reload">
              <RefreshCw className={`w-3.5 h-3.5 ${loadingMor ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-orange-50 text-orange-900">
              <tr>
                <th className="text-left p-2 w-12">#</th>
                <th className="text-left p-2">Contribuyente</th>
                <th className="text-left p-2">RIF</th>
                <th className="text-left p-2">Actividad</th>
                <th className="text-right p-2">Planillas</th>
                <th className="text-right p-2">Deuda (Bs.)</th>
                <th className="text-center p-2">Último contacto</th>
                <th className="text-center p-2">Acción</th>
              </tr>
            </thead>
            <tbody>
              {(morosos?.rows || []).map((m, i) => (
                <tr key={m.contribuyente_id} className="border-b border-orange-50 hover:bg-orange-50/40" data-testid={`moroso-row-${i}`}>
                  <td className="p-2 font-mono text-xs text-orange-700 font-bold">{i + 1}</td>
                  <td className="p-2"><span className="font-medium">{m.nombre}</span>{m.numero_lae && <span className="ml-2 text-xs text-slate-500 font-mono">LAE {m.numero_lae}</span>}</td>
                  <td className="p-2 font-mono text-xs">{m.rif || "—"}</td>
                  <td className="p-2 text-xs text-slate-600">{(m.actividad || "").slice(0, 30)}</td>
                  <td className="p-2 text-right font-mono">{m.planillas_pendientes}</td>
                  <td className="p-2 text-right font-mono font-bold text-orange-700">{fmt(m.deuda_total)}</td>
                  <td className="p-2 text-center text-xs">
                    {m.ultimo_contacto ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700" data-testid={`moroso-contacto-${i}`}>
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>{hace(m.ultimo_contacto)}</span>
                        {m.total_intentos_contacto > 1 && <span className="text-slate-400">({m.total_intentos_contacto})</span>}
                      </span>
                    ) : (
                      <span className="text-slate-400" data-testid={`moroso-contacto-${i}`}>Sin contactar</span>
                    )}
                  </td>
                  <td className="p-2 text-center">
                    {m.tiene_telefono ? (
                      <a href={m.whatsapp_url} target="_blank" rel="noopener noreferrer"
                        onClick={() => marcarContactado(m, "whatsapp")}
                        data-testid={`moroso-wa-${i}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-green-600 hover:bg-green-700 text-white text-xs font-medium transition-colors">
                        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                      </a>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-slate-100 text-slate-400 text-xs" title="Sin teléfono registrado">
                        <PhoneOff className="w-3.5 h-3.5" /> Sin tel.
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {!morosos?.rows?.length && !loadingMor && (
                <tr><td colSpan={8} className="p-8 text-center text-green-600">🎉 No hay contribuyentes con deuda pendiente</td></tr>
              )}
              {loadingMor && (
                <tr><td colSpan={8} className="p-8 text-center text-orange-500">Cargando ranking…</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {morosos?.rows?.length > 0 && (
          <p className="text-xs text-orange-700/60 mt-3">
            💡 Al hacer clic en WhatsApp se abre la conversación con mensaje pre-cargado <b>y</b> se registra automáticamente el intento de contacto para seguimiento. Use «Exportar todos» para descargar el ranking completo en Excel con los links de WhatsApp por deudor.
          </p>
        )}
      </Card>
    </div>
  );
}

// ============================================================================
// REPORTE 2 — Recaudación por Cuentas Recaudadoras
// ============================================================================
const COLORS = ["#16A34A", "#EA580C", "#0284C7", "#7C3AED", "#DB2777", "#CA8A04", "#059669", "#2563EB", "#DC2626", "#9333EA"];

export function ReporteCuentasRecaudadoras({ auth, API, canExport }) {
  const [f, setF] = useState({ desde: "", hasta: "", estado: "validado" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reportes/recaudacion-por-cuenta`, { ...auth, params: f });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al cargar"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const rows = data?.rows || [];
  const totales = data?.totales || {};
  const chartData = rows.map((r) => ({
    name: `${r.banco_nombre} · ${r.tipo}`.slice(0, 30),
    value: r.monto_total,
  }));

  const exportar = async (formato) => {
    if (!rows.length) { toast.error("No hay datos"); return; }
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const fn = `reporte_cuentas_recaudadoras_${stamp}`;
    if (formato === "excel") {
      const ws = XLSX.utils.json_to_sheet(rows.map(r => ({
        "Banco": r.banco_nombre, "Tipo": r.tipo, "N° Cuenta": r.nro_cuenta, "Titular": r.titular,
        "Cantidad Pagos": r.cantidad_pagos, "Monto (Bs.)": r.monto_total, "% del total": r.porcentaje,
      })));
      const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Cuentas");
      XLSX.writeFile(wb, `${fn}.xlsx`); toast.success(`Excel descargado (${rows.length} cuentas)`);
    } else if (formato === "pdf") {
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const { fecha, hora } = fechaHoraVE();
      const logo = await loadPalmaLogo();
      doc.setFontSize(16); doc.setTextColor(20, 83, 45);
      doc.text("SISAUR · Recaudación por Cuentas Recaudadoras", 40, 40);
      doc.setFontSize(10); doc.setTextColor(80);
      doc.text(`Generado: ${fecha} ${hora}  ·  Rango: ${f.desde || "sin desde"} → ${f.hasta || "sin hasta"}  ·  Estado: ${f.estado}`, 40, 58);
      doc.setFontSize(11); doc.setTextColor(20, 83, 45);
      doc.text(`Total recaudado: Bs. ${fmt(totales.total_monto)}   ·   Pagos: ${totales.total_pagos}   ·   Cuentas: ${totales.cuentas_activas}`, 40, 78);
      autoTable(doc, {
        head: [["Banco", "Tipo", "N° Cuenta", "Titular", "Pagos", "Monto (Bs.)", "% Total"]],
        body: rows.map(r => [r.banco_nombre, r.tipo, r.nro_cuenta, r.titular, r.cantidad_pagos, fmt(r.monto_total), `${r.porcentaje}%`]),
        startY: 95, styles: { fontSize: 8 }, headStyles: { fillColor: [22, 163, 74] },
      });
      drawPalmaFooter(doc, logo);
      doc.save(`${fn}.pdf`); toast.success("PDF descargado");
    } else if (formato === "json") {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${fn}.json`; a.click(); URL.revokeObjectURL(url);
      toast.success("JSON descargado");
    }
  };

  return (
    <div className="space-y-6" data-testid="reporte-cuentas-container">
      <Card className="p-4 border-green-100">
        <p className="text-sm font-medium text-green-900 mb-3">Filtros</p>
        <div className="grid md:grid-cols-5 gap-3">
          <div><Label className="text-xs">Desde</Label><Input type="date" value={f.desde} onChange={e => setF({ ...f, desde: e.target.value })} data-testid="cta-desde" /></div>
          <div><Label className="text-xs">Hasta</Label><Input type="date" value={f.hasta} onChange={e => setF({ ...f, hasta: e.target.value })} data-testid="cta-hasta" /></div>
          <div><Label className="text-xs">Estado</Label>
            <Select value={f.estado} onValueChange={v => setF({ ...f, estado: v })}>
              <SelectTrigger data-testid="cta-estado"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="validado">Validados</SelectItem>
                <SelectItem value="pendiente_validacion">Pendientes</SelectItem>
                <SelectItem value="todos">Todos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2 col-span-2">
            <Button onClick={load} disabled={loading} className="bg-green-700 hover:bg-green-800 text-white flex-1" data-testid="cta-aplicar"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />Aplicar</Button>
            <Popover>
              <PopoverTrigger asChild><Button variant="outline" data-testid="cta-exportar" disabled={!rows.length || !canExport}><Download className="w-4 h-4" /></Button></PopoverTrigger>
              <PopoverContent align="end" className="w-52 p-1">
                <button onClick={() => exportar("excel")} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-green-50 text-left"><FileSpreadsheet className="w-4 h-4 text-green-700" /> Excel</button>
                <button onClick={() => exportar("pdf")} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-orange-50 text-left"><FileText className="w-4 h-4 text-orange-600" /> PDF</button>
                <button onClick={() => exportar("json")} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-slate-50 text-left"><FileJson className="w-4 h-4 text-slate-600" /> JSON</button>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-3 gap-4">
        <KPI icon={DollarSign} label="Total Recaudado" value={`Bs. ${fmt(totales.total_monto)}`} hint={`${totales.total_pagos || 0} pagos`} color="green" />
        <KPI icon={Banknote}   label="Cuentas Activas" value={totales.cuentas_activas || 0} hint="Con al menos 1 pago" color="blue" />
        <KPI icon={AlertTriangle} label="Sin cuenta asignada" value={totales.sin_cuenta_asignada?.cantidad || 0} hint={`Bs. ${fmt(totales.sin_cuenta_asignada?.monto)}`} color="orange" />
      </div>

      {rows.length > 0 && (
        <Card className="p-4 border-green-100">
          <p className="font-medium text-green-900 mb-3">Distribución de la recaudación por cuenta</p>
          <div className="h-72" data-testid="cta-chart-pie">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({name, percent}) => `${(percent*100).toFixed(1)}%`}>
                  {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `Bs. ${fmt(v)}`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      <Card className="p-4 border-green-100">
        <p className="font-medium text-green-900 mb-3">Detalle por Cuenta Recaudadora <span className="text-sm font-normal text-green-700/70">({rows.length} cuentas)</span></p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-green-50 text-green-900">
              <tr>
                <th className="text-left p-2">Banco</th>
                <th className="text-left p-2">Tipo</th>
                <th className="text-left p-2">N° Cuenta</th>
                <th className="text-left p-2">Titular</th>
                <th className="text-right p-2">Pagos</th>
                <th className="text-right p-2">Monto (Bs.)</th>
                <th className="text-right p-2">% Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan="7" className="text-center text-slate-500 py-6">Sin datos en el rango seleccionado</td></tr>}
              {rows.map((r, i) => (
                <tr key={r.cuenta_id} className="border-b border-slate-100 hover:bg-green-50/30" data-testid={`cta-row-${i}`}>
                  <td className="p-2 font-medium text-green-900">{r.banco_nombre}</td>
                  <td className="p-2"><span className="inline-block px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-800 font-medium">{r.tipo}</span></td>
                  <td className="p-2 font-mono text-xs">{r.nro_cuenta}</td>
                  <td className="p-2 text-slate-700">{r.titular}</td>
                  <td className="p-2 text-right font-mono">{r.cantidad_pagos}</td>
                  <td className="p-2 text-right font-mono font-semibold text-green-800">{fmt(r.monto_total)}</td>
                  <td className="p-2 text-right font-mono text-orange-700">{r.porcentaje}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}


// ============================================================================
// REPORTE 3 — Pagos Taquilla vs Online
// ============================================================================
export function ReporteTaquillaVsOnline({ auth, API, canExport }) {
  const [f, setF] = useState({ desde: "", hasta: "", estado: "validado" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reportes/pagos-taquilla-vs-online`, { ...auth, params: f });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al cargar"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const resumen = data?.resumen || {};
  const totales = data?.totales || {};
  const porMes = data?.por_mes || [];
  const porMetodo = data?.por_metodo || [];
  const topFunc = data?.top_funcionarios_taquilla || [];

  const pieData = [
    { name: "Taquilla (Funcionarios)", value: resumen.taquilla?.monto || 0, color: "#16A34A" },
    { name: "Online (Portal Contribuyente)", value: resumen.online?.monto || 0, color: "#EA580C" },
  ];

  const exportar = async (formato) => {
    if (!totales.cantidad) { toast.error("No hay datos"); return; }
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const fn = `reporte_taquilla_vs_online_${stamp}`;
    if (formato === "excel") {
      const wb = XLSX.utils.book_new();
      const wsResumen = XLSX.utils.json_to_sheet([
        { Canal: "Taquilla (Funcionarios)", Cantidad: resumen.taquilla?.cantidad, "Monto (Bs.)": resumen.taquilla?.monto, "% Monto": resumen.taquilla?.porcentaje_monto, "% Cantidad": resumen.taquilla?.porcentaje_cantidad },
        { Canal: "Online (Portal Contribuyente)", Cantidad: resumen.online?.cantidad, "Monto (Bs.)": resumen.online?.monto, "% Monto": resumen.online?.porcentaje_monto, "% Cantidad": resumen.online?.porcentaje_cantidad },
      ]);
      XLSX.utils.book_append_sheet(wb, wsResumen, "Resumen");
      if (porMes.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(porMes), "Por Mes");
      if (porMetodo.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(porMetodo), "Por Método");
      if (topFunc.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(topFunc), "Top Funcionarios");
      XLSX.writeFile(wb, `${fn}.xlsx`); toast.success("Excel descargado");
    } else if (formato === "pdf") {
      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const { fecha, hora } = fechaHoraVE();
      const logo = await loadPalmaLogo();
      doc.setFontSize(16); doc.setTextColor(20, 83, 45);
      doc.text("SISAUR · Recaudación · Taquilla vs Online", 40, 40);
      doc.setFontSize(10); doc.setTextColor(80);
      doc.text(`Generado: ${fecha} ${hora}  ·  Rango: ${f.desde || "sin desde"} → ${f.hasta || "sin hasta"}  ·  Estado: ${f.estado}`, 40, 58);
      autoTable(doc, {
        head: [["Canal", "Cantidad", "Monto (Bs.)", "% Monto", "% Cantidad"]],
        body: [
          ["Taquilla (Funcionarios)", resumen.taquilla?.cantidad || 0, fmt(resumen.taquilla?.monto), `${resumen.taquilla?.porcentaje_monto}%`, `${resumen.taquilla?.porcentaje_cantidad}%`],
          ["Online (Portal Contribuyente)", resumen.online?.cantidad || 0, fmt(resumen.online?.monto), `${resumen.online?.porcentaje_monto}%`, `${resumen.online?.porcentaje_cantidad}%`],
        ],
        startY: 80, headStyles: { fillColor: [22, 163, 74] }, styles: { fontSize: 9 },
      });
      if (porMes.length) {
        autoTable(doc, {
          head: [["Mes", "Taquilla (cant)", "Taquilla (Bs.)", "Online (cant)", "Online (Bs.)"]],
          body: porMes.map(m => [m.mes, m.taquilla_cantidad, fmt(m.taquilla_monto), m.online_cantidad, fmt(m.online_monto)]),
          startY: doc.lastAutoTable.finalY + 20, headStyles: { fillColor: [234, 88, 12] }, styles: { fontSize: 8 },
        });
      }
      drawPalmaFooter(doc, logo);
      doc.save(`${fn}.pdf`); toast.success("PDF descargado");
    } else if (formato === "json") {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${fn}.json`; a.click(); URL.revokeObjectURL(url);
      toast.success("JSON descargado");
    }
  };

  return (
    <div className="space-y-6" data-testid="reporte-canal-container">
      <Card className="p-4 border-green-100">
        <p className="text-sm font-medium text-green-900 mb-3">Filtros</p>
        <div className="grid md:grid-cols-5 gap-3">
          <div><Label className="text-xs">Desde</Label><Input type="date" value={f.desde} onChange={e => setF({ ...f, desde: e.target.value })} data-testid="tvo-desde" /></div>
          <div><Label className="text-xs">Hasta</Label><Input type="date" value={f.hasta} onChange={e => setF({ ...f, hasta: e.target.value })} data-testid="tvo-hasta" /></div>
          <div><Label className="text-xs">Estado</Label>
            <Select value={f.estado} onValueChange={v => setF({ ...f, estado: v })}>
              <SelectTrigger data-testid="tvo-estado"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="validado">Validados</SelectItem>
                <SelectItem value="pendiente_validacion">Pendientes</SelectItem>
                <SelectItem value="todos">Todos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2 col-span-2">
            <Button onClick={load} disabled={loading} className="bg-green-700 hover:bg-green-800 text-white flex-1" data-testid="tvo-aplicar"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />Aplicar</Button>
            <Popover>
              <PopoverTrigger asChild><Button variant="outline" data-testid="tvo-exportar" disabled={!totales.cantidad || !canExport}><Download className="w-4 h-4" /></Button></PopoverTrigger>
              <PopoverContent align="end" className="w-52 p-1">
                <button onClick={() => exportar("excel")} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-green-50 text-left"><FileSpreadsheet className="w-4 h-4 text-green-700" /> Excel</button>
                <button onClick={() => exportar("pdf")} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-orange-50 text-left"><FileText className="w-4 h-4 text-orange-600" /> PDF</button>
                <button onClick={() => exportar("json")} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-slate-50 text-left"><FileJson className="w-4 h-4 text-slate-600" /> JSON</button>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-4 gap-4">
        <KPI icon={Users2}     label="Taquilla · Cantidad" value={resumen.taquilla?.cantidad || 0} hint={`${resumen.taquilla?.porcentaje_cantidad || 0}% del total`} color="green" />
        <KPI icon={DollarSign} label="Taquilla · Monto"    value={`Bs. ${fmt(resumen.taquilla?.monto)}`} hint={`${resumen.taquilla?.porcentaje_monto || 0}% del monto`} color="green" />
        <KPI icon={Users2}     label="Online · Cantidad"   value={resumen.online?.cantidad || 0} hint={`${resumen.online?.porcentaje_cantidad || 0}% del total`} color="orange" />
        <KPI icon={DollarSign} label="Online · Monto"      value={`Bs. ${fmt(resumen.online?.monto)}`} hint={`${resumen.online?.porcentaje_monto || 0}% del monto`} color="orange" />
      </div>

      {(resumen.taquilla?.monto || resumen.online?.monto) ? (
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="p-4 border-green-100">
            <p className="font-medium text-green-900 mb-3">Distribución por canal</p>
            <div className="h-72" data-testid="tvo-chart-pie">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({name, percent}) => `${(percent*100).toFixed(1)}%`}>
                    {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `Bs. ${fmt(v)}`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card className="p-4 border-green-100">
            <p className="font-medium text-green-900 mb-3">Evolución mensual</p>
            <div className="h-72" data-testid="tvo-chart-mes">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={porMes}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={fmt} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => `Bs. ${fmt(v)}`} />
                  <Legend />
                  <Bar dataKey="taquilla_monto" name="Taquilla" fill="#16A34A" />
                  <Bar dataKey="online_monto" name="Online" fill="#EA580C" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      ) : null}

      {porMetodo.length > 0 && (
        <Card className="p-4 border-green-100">
          <p className="font-medium text-green-900 mb-3">Desglose por método de pago</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-green-50 text-green-900">
                <tr>
                  <th className="text-left p-2">Método</th>
                  <th className="text-right p-2">Taquilla (cant)</th>
                  <th className="text-right p-2">Taquilla (Bs.)</th>
                  <th className="text-right p-2">Online (cant)</th>
                  <th className="text-right p-2">Online (Bs.)</th>
                </tr>
              </thead>
              <tbody>
                {porMetodo.map((m, i) => (
                  <tr key={i} className="border-b border-slate-100" data-testid={`tvo-metodo-${i}`}>
                    <td className="p-2 font-medium capitalize">{(m.metodo || "").replace("_", " ")}</td>
                    <td className="p-2 text-right font-mono">{m.taquilla_cantidad}</td>
                    <td className="p-2 text-right font-mono text-green-800">{fmt(m.taquilla_monto)}</td>
                    <td className="p-2 text-right font-mono">{m.online_cantidad}</td>
                    <td className="p-2 text-right font-mono text-orange-700">{fmt(m.online_monto)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {topFunc.length > 0 && (
        <Card className="p-4 border-green-100">
          <p className="font-medium text-green-900 mb-3">Top funcionarios · Taquilla</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-green-50 text-green-900">
                <tr>
                  <th className="text-left p-2">#</th>
                  <th className="text-left p-2">Funcionario</th>
                  <th className="text-right p-2">Pagos gestionados</th>
                  <th className="text-right p-2">Monto (Bs.)</th>
                </tr>
              </thead>
              <tbody>
                {topFunc.map((t, i) => (
                  <tr key={i} className="border-b border-slate-100" data-testid={`tvo-func-${i}`}>
                    <td className="p-2 text-slate-500">{i + 1}</td>
                    <td className="p-2 font-medium text-green-900">{t.funcionario}</td>
                    <td className="p-2 text-right font-mono">{t.cantidad}</td>
                    <td className="p-2 text-right font-mono font-semibold text-green-800">{fmt(t.monto)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

